import React, { useState, useEffect, useRef } from "react";
import {
  ArrowLeft,
  Plus,
  Edit,
  Calendar,
  CheckCircle,
  XCircle,
  Filter,
} from "lucide-react";
import ReferralService from "../../../services/api/admin.referral.service";

const ReferralRewardConfig = ({ onBack }) => {
  const [loading, setLoading] = useState(true);
  const [rewardConfigs, setRewardConfigs] = useState([]);
  const [activeConfig, setActiveConfig] = useState(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [formMode, setFormMode] = useState("create"); // 'create' or 'edit'
  const [selectedConfig, setSelectedConfig] = useState(null);
  const formRef = useRef(null);
  const [filterStatus, setFilterStatus] = useState("ALL"); // "ALL", "ACTIVE", or "INACTIVE"
  const [formData, setFormData] = useState({
    rewardAmount: "",
    minimumTransactionAmount: "",
    startDate: "",
    endDate: "",
    status: "INACTIVE",
    description: "",
    configId: null,
  });
  const [formErrors, setFormErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState("");

  useEffect(() => {
    fetchRewardConfigs();
  }, [filterStatus]); // Refetch when filter changes

  const fetchRewardConfigs = async () => {
    try {
      setLoading(true);
      // Use the new API endpoint that supports filtering by status
      const response = await ReferralService.getReferralRewardConfigs(
        filterStatus
      );

      if (response.success && response.data) {
        // Ensure we always work with an array
        const configsArray = Array.isArray(response.data)
          ? response.data
          : response.data.configurations ||
          (response.data._id ? [response.data] : []);

        setRewardConfigs(configsArray);

        // Find active configuration (if any)
        const active = configsArray.find(
          (config) =>
            config.status === "ACTIVE" &&
            new Date(config.startDate) <= new Date() &&
            new Date(config.endDate) >= new Date()
        );

        setActiveConfig(active || null);
      }
    } catch (error) {
      console.error("Error fetching reward configurations:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Clear error for this field if exists
    if (formErrors[name]) {
      setFormErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const validateForm = () => {
    const errors = {};

    if (
      !formData.rewardAmount ||
      isNaN(formData.rewardAmount) ||
      formData.rewardAmount <= 0
    ) {
      errors.rewardAmount = "Please enter a valid reward amount";
    }

    if (
      formData.minimumTransactionAmount === "" ||
      isNaN(formData.minimumTransactionAmount) ||
      formData.minimumTransactionAmount < 0
    ) {
      errors.minimumTransactionAmount = "Please enter a valid minimum transaction amount";
    }
    if (!formData.startDate) {
      errors.startDate = "Start date is required";
    }

    if (!formData.endDate) {
      errors.endDate = "End date is required";
    }

    if (
      formData.startDate &&
      formData.endDate &&
      new Date(formData.startDate) >= new Date(formData.endDate)
    ) {
      errors.dateRange = "End date must be after start date";
    }

    if (!formData.description) {
      errors.description = "Description is required";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      setSuccessMessage("");

      const payload = {
        ...formData,
        rewardAmount: parseFloat(formData.rewardAmount),
        minimumTransactionAmount: parseFloat(formData.minimumTransactionAmount),
      };

      const response = await ReferralService.createOrUpdateRewardConfig(
        payload
      );

      if (response.success) {
        setSuccessMessage(
          formMode === "create"
            ? "Reward configuration created successfully!"
            : "Reward configuration updated successfully!"
        );

        // Reset form and close it
        setFormData({
          rewardAmount: "",
          minimumTransactionAmount: "",
          startDate: "",
          endDate: "",
          status: "INACTIVE",
          description: "",
          configId: null,
        });

        setIsFormOpen(false);

        // Refresh configurations list
        fetchRewardConfigs();
      }
    } catch (error) {
      console.error("Error saving reward configuration:", error);
      if (error.response?.data?.errors) {
        // Process validation errors from backend
        const backendErrors = {};
        error.response.data.errors.forEach((err) => {
          backendErrors[err.field] = err.message;
        });
        setFormErrors(backendErrors);
      } else {
        setFormErrors({
          general: "Failed to save configuration. Please try again.",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (config) => {
    const formatDate = (dateString) => {
      const date = new Date(dateString);
      return date.toISOString().split("T")[0];
    };

    // Set form data and mode here...
    setFormMode("edit");
    setFormData({
      ...config,
      minimumTransactionAmount: config.minimumTransactionAmount ?? 1000,
      startDate: formatDate(config.startDate),
      endDate: formatDate(config.endDate),
    });

    setIsFormOpen(true);

    // Scroll to form after a short delay to ensure it's rendered
    setTimeout(() => {
      formRef.current?.scrollIntoView({ behavior: "smooth" });
    }, 100);
  };

  const handleCreateNew = () => {
    setFormData({
      rewardAmount: "",
      minimumTransactionAmount: "",
      startDate: "",
      endDate: "",
      status: "INACTIVE",
      description: "",
      configId: null,
    });

    setFormMode("create");
    setSelectedConfig(null);
    setIsFormOpen(true);
    setFormErrors({});
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // Determine config status with date consideration
  const getConfigStatus = (config) => {
    const now = new Date();
    const startDate = new Date(config.startDate);
    const endDate = new Date(config.endDate);

    if (config.status === "ACTIVE") {
      if (now < startDate) {
        return { status: "SCHEDULED", class: "bg-blue-100 text-blue-800" };
      } else if (now > endDate) {
        return { status: "EXPIRED", class: "bg-orange-100 text-orange-800" };
      } else {
        return { status: "ACTIVE", class: "bg-green-100 text-green-800" };
      }
    }

    return { status: "INACTIVE", class: "bg-gray-100 text-gray-800" };
  };

  return (
    <div className="p-6">
      {/* Success message */}
      {successMessage && (
        <div className="mb-6 p-4 bg-green-100 text-green-700 rounded-md flex items-center">
          <CheckCircle className="w-5 h-5 mr-2" />
          {successMessage}
        </div>
      )}

      {/* Active Configuration Card */}
      <div ref={formRef} className="mb-8">
        <h2 className="text-lg font-medium mb-4">Active Configuration</h2>
        {loading ? (
          <div className="flex justify-center items-center py-6">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-700"></div>
          </div>
        ) : activeConfig ? (
          <div className="bg-green-50 border border-green-200 rounded-lg p-6">
            <div className="flex justify-between items-start">
              <div>
                <h3 className="text-xl font-semibold text-green-800">
                  ₹{activeConfig.rewardAmount} Reward
                </h3>
                <p className="text-sm font-medium text-green-700 mt-1 mb-3">
                  Min. Investment Required: ₹{(activeConfig.minimumTransactionAmount ?? 1000).toLocaleString("en-IN")}
                </p>
                <p className="text-sm text-green-700 mb-4">
                  {activeConfig.description}
                </p>

                <div className="flex items-center mb-2">
                  <Calendar className="w-4 h-4 mr-2 text-green-600" />
                  <span className="text-sm text-green-700">
                    Valid from {formatDate(activeConfig.startDate)} to{" "}
                    {formatDate(activeConfig.endDate)}
                  </span>
                </div>

                <div className="mt-4">
                  <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs">
                    ACTIVE
                  </span>
                </div>
              </div>

              <button
                onClick={() => handleEdit(activeConfig)}
                className="p-2 text-green-600 hover:text-green-800 hover:bg-green-100 rounded-full"
              >
                <Edit className="w-5 h-5" />
              </button>
            </div>
          </div>
        ) : (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center">
            <p className="text-gray-600">
              No active reward configuration found.
            </p>
            <button
              onClick={handleCreateNew}
              className="mt-4 flex items-center justify-center mx-auto px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              <Plus className="w-4 h-4 mr-2" /> Create New Configuration
            </button>
          </div>
        )}
      </div>

      {/* Configuration Form */}
      {isFormOpen && (
        <div className="mb-8 bg-white border rounded-lg p-6 shadow-sm">
          <h2 className="text-lg font-medium mb-4">
            {formMode === "create"
              ? "Create New Configuration"
              : "Edit Configuration"}
          </h2>

          <form onSubmit={handleSubmit}>
            {/* General form error */}
            {formErrors.general && (
              <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-md">
                {formErrors.general}
              </div>
            )}

            {/* Date range error */}
            {formErrors.dateRange && (
              <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-md">
                {formErrors.dateRange}
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              {/* Reward Amount */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Reward Amount (₹)
                </label>
                <input
                  type="number"
                  name="rewardAmount"
                  value={formData.rewardAmount}
                  onFocus={(e) => e.target.select()}
                  onChange={handleInputChange}
                  className={`w-full border ${formErrors.rewardAmount
                    ? "border-red-500"
                    : "border-gray-300"
                    } rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
                  placeholder="Enter reward amount"
                />
                {formErrors.rewardAmount && (
                  <p className="mt-1 text-sm text-red-600">
                    {formErrors.rewardAmount}
                  </p>
                )}
              </div>

              {/* Minimum Transaction Amount */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Minimum Investment Amount (₹)
                </label>
                <input
                  type="number"
                  name="minimumTransactionAmount"
                  value={formData.minimumTransactionAmount}
                  onFocus={(e) => e.target.select()}
                  onChange={handleInputChange}
                  className={`w-full border ${formErrors.minimumTransactionAmount
                    ? "border-red-500"
                    : "border-gray-300"
                    } rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
                  placeholder="Min. investment to qualify (e.g. 5000)"
                />
                {formErrors.minimumTransactionAmount && (
                  <p className="mt-1 text-sm text-red-600">
                    {formErrors.minimumTransactionAmount}
                  </p>
                )}
              </div>

              {/* Status */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="ACTIVE">Active</option>
                  <option value="INACTIVE">Inactive</option>
                </select>
              </div>

              {/* Start Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Start Date
                </label>
                <input
                  type="date"
                  name="startDate"
                  value={formData.startDate}
                  onChange={handleInputChange}
                  className={`w-full border ${formErrors.startDate ? "border-red-500" : "border-gray-300"
                    } rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
                />
                {formErrors.startDate && (
                  <p className="mt-1 text-sm text-red-600">
                    {formErrors.startDate}
                  </p>
                )}
              </div>

              {/* End Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  End Date
                </label>
                <input
                  type="date"
                  name="endDate"
                  value={formData.endDate}
                  onChange={handleInputChange}
                  className={`w-full border ${formErrors.endDate ? "border-red-500" : "border-gray-300"
                    } rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
                />
                {formErrors.endDate && (
                  <p className="mt-1 text-sm text-red-600">
                    {formErrors.endDate}
                  </p>
                )}
              </div>

              {/* Description */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  className={`w-full border ${formErrors.description
                    ? "border-red-500"
                    : "border-gray-300"
                    } rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
                  rows="3"
                  placeholder="Enter configuration description"
                ></textarea>
                {formErrors.description && (
                  <p className="mt-1 text-sm text-red-600">
                    {formErrors.description}
                  </p>
                )}
              </div>
            </div>

            {/* Form actions */}
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => setIsFormOpen(false)}
                className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-100"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Saving...
                  </>
                ) : (
                  <>Save Configuration</>
                )}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* All Configurations */}
      <div>
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-medium">All Configurations</h2>

          <div className="flex items-center gap-4">
            {/* Filter Dropdown */}
            <div className="flex items-center">
              <Filter className="w-4 h-4 mr-2 text-gray-500" />
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="border border-gray-300 rounded-md px-3 py-1 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="ALL">All</option>
                <option value="ACTIVE">Active</option>
                <option value="INACTIVE">Inactive</option>
              </select>
            </div>

            {!isFormOpen && (
              <button
                onClick={handleCreateNew}
                className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                <Plus className="w-4 h-4 mr-2" /> Create New
              </button>
            )}
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-6">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-700"></div>
          </div>
        ) : rewardConfigs.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Reward Amount
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Min. Investment
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Description
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Date Range
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Status
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {rewardConfigs.map((config) => {
                  const configStatus = getConfigStatus(config);
                  return (
                    <tr key={config._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        ₹{config.rewardAmount}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 font-medium">
                        ₹{(config.minimumTransactionAmount ?? 1000).toLocaleString("en-IN")}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate">
                        {config.description}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(config.startDate)} -{" "}
                        {formatDate(config.endDate)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <span
                          className={`px-2 py-1 rounded-full text-xs ${configStatus.class}`}
                        >
                          {configStatus.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <button
                          onClick={() => handleEdit(config)}
                          className="text-blue-600 hover:text-blue-800"
                        >
                          Edit
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg">
            No reward configurations found.
          </div>
        )}
      </div>
    </div>
  );
};

export default ReferralRewardConfig;
